import { useParams, Link } from "react-router-dom";
import { PublicLayout } from "@/components/layout/PublicLayout";
import SEO from "@/components/SEO";
import { container } from "@/lib/constants";

const CONTENT = {
  terms: {
    title: "Terms of Access",
    intro: "These terms govern access to and use of Prime Deal Gateway by verified principals and their authorised representatives.",
    sections: [
      ["Eligibility", "Access is by invitation and extended only to verified principals — family offices, private investment vehicles, and operators. We reserve the right to decline or revoke access at our sole discretion."],
      ["No offer or advice", "Nothing on this platform constitutes an offer, solicitation, or recommendation to buy or sell any security or asset, nor investment, legal, or tax advice. All opportunities are provided for information only."],
      ["Confidentiality", "All deal information is confidential. Members must not disclose, reproduce, or distribute any information accessed through the platform. Document access is governed by separate non-disclosure agreements."],
      ["Jurisdictions", "The platform is intended for professional and institutional counterparties in the United Kingdom, the European Union, the United Arab Emirates, Singapore, and the United States, subject to local law."],
    ],
  },
  privacy: {
    title: "Privacy Policy",
    intro: "We take the privacy of principals and their representatives seriously. This policy explains what we collect and how we use it.",
    sections: [
      ["Information we collect", "Application and verification details, KYC documentation, mandate information, and platform activity necessary to operate the desk and meet our regulatory obligations."],
      ["How we use it", "To verify status, match opportunities to mandates, coordinate introductions, maintain audit trails, and communicate with you. We do not sell personal data."],
      ["Retention & security", "Data is retained for as long as necessary to meet legal and operational obligations and is protected with access controls, encryption in transit, and immutable audit logging."],
      ["Your rights", "Subject to jurisdiction, you may request access to, correction of, or deletion of your personal data. Contact the desk to exercise these rights."],
    ],
  },
  cookies: {
    title: "Cookie Policy",
    intro: "We use a minimal set of cookies and local storage to operate the platform securely.",
    sections: [
      ["Essential", "Authentication tokens and session state required to keep you signed in and to protect your account. These cannot be disabled without preventing access."],
      ["Analytics", "Aggregate, privacy-respecting analytics help us understand platform usage. These are configured to avoid identifying individuals wherever possible."],
      ["Managing cookies", "You may control cookies through your browser settings. Disabling essential cookies will prevent you from signing in."],
    ],
  },
  risk: {
    title: "Risk Disclosure",
    intro: "Private-market investing involves significant risk and is suitable only for sophisticated, professional, and institutional investors.",
    sections: [
      ["Capital at risk", "The value of private-market investments can fall as well as rise. You may lose some or all of your capital. Past performance is not a guide to future results."],
      ["Illiquidity", "Private-market opportunities are typically illiquid and long-term. There may be no secondary market and you may be unable to realise your investment when desired."],
      ["No guarantees", "Projected returns, target IRRs, and metrics are indicative only and are not guaranteed. Diligence remains the responsibility of each principal and their advisers."],
      ["Professional counterparties only", "Opportunities are made available only to verified professional and institutional counterparties. Nothing here is directed at retail investors."],
    ],
  },
};

export default function Legal() {
  const { type } = useParams();
  const c = CONTENT[type] || CONTENT.terms;
  return (
    <PublicLayout>
      <SEO title={c.title} description={c.intro.slice(0, 155)} />
      <article className={`${container} py-20 lg:py-24 max-w-3xl`}>
        <div className="pdg-kicker">Legal</div>
        <h1 className="mt-4 pdg-serif text-4xl sm:text-5xl text-foreground leading-[1.08]">{c.title}</h1>
        <p className="mt-6 text-base text-foreground/80 leading-8">{c.intro}</p>
        <div className="mt-8 pdg-hairline w-20" />
        <div className="mt-10 space-y-10">
          {c.sections.map(([h, b]) => (
            <section key={h}>
              <h2 className="pdg-serif text-2xl text-foreground">{h}</h2>
              <p className="mt-3 text-sm text-muted-foreground leading-8">{b}</p>
            </section>
          ))}
        </div>
        <p className="mt-14 text-xs text-muted-foreground">Last updated {new Date().toLocaleDateString("en-GB", { year: "numeric", month: "long" })}. Questions? <Link to="/contact" className="text-[hsl(var(--pdg-gold))]">Contact the desk</Link>.</p>
      </article>
    </PublicLayout>
  );
}
